import styled from "@emotion/styled";
import CodeIcon from "@mui/icons-material/Code";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import { Box, Button, Divider, IconButton, Stack, Typography, useTheme } from "@mui/material";
import { JWT_KEY } from "Auth/Models/AuthConstants";
import { DevTools } from "Components/_Shared/DevTools/DevTools";
import { Popper } from "Components/_Shared/Popper";
import { UnstyledLink } from "Components/_Shared/Routing/UnstyledLink";
import { UserInitialsAvatar } from "Components/_Shared/UserInitialsAvatar";
import { useLogoutZfpSso } from "Infrastructure/ApiZfp/AnalyzerApi";
import { Configuration } from "Infrastructure/Config/config";
import { useUserContext } from "Infrastructure/Context/UserContext";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { AppRouting } from "Infrastructure/Utils/UrlUtils";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ReactComponent as AtIcon } from "../Icons/Assets/at.svg";
import { ReactComponent as HomeIcon } from "../Icons/Assets/home.svg";
import { ReactComponent as LogoutIcon } from "../Icons/Assets/logout.svg";
import { ReactComponent as PhoneIcon } from "../Icons/Assets/phone.svg";
import { UserProfilePictureIcon } from "../UserProfilePictureIcon";

const StyledLink = styled(Button)`
  ${({ theme }) => `
    display: flex;
    justify-content: flex-start;
    height: ${theme.spacing(9)};
    border-radius: ${theme.spacing(Spacing.M)};
    padding: ${theme.spacing(0, Spacing.S)};
  `}
`;

export const UserMenu = () => {
  const { refetch: fetchLogoutUrl } = useLogoutZfpSso(
    { redirectUrl: `${window.location.protocol}//${window.location.host}` },
    { query: { enabled: false } },
  );
  const { t } = useResource();
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useUserContext();
  const profilePicture = user?.profilePictureUrl ?? null;
  const isDevelopment = Configuration.getIsDevelopment();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDevToolsOpen, setIsDevToolsOpen] = useState(false);

  // the popper sits above MUI dialogs (z-index 10000), so it has to close before DevTools opens
  const handleOpenDevTools = () => {
    setIsMenuOpen(false);
    setIsDevToolsOpen(true);
  };

  const handleLogout = async () => {
    try {
      const { data } = await fetchLogoutUrl();
      if (data?.url) {
        window.location.replace(data.url);
      }
    } catch (error) {
      console.error(error);
    } finally {
      localStorage.removeItem(JWT_KEY);
    }
  };

  const fullName = user ? `${user.firstName} ${user.lastName}` : "";

  return (
    <>
      <Popper
        noPadding
        isOpen={isMenuOpen}
        onOpen={() => setIsMenuOpen(true)}
        btnEl={
          <IconButton aria-label={t(Resources.UserMenu.Open)} sx={{ padding: 0 }}>
            {profilePicture ? (
              <UserProfilePictureIcon profilePicture={profilePicture} />
            ) : (
              <UserInitialsAvatar firstName={user?.firstName} surname={user?.lastName} />
            )}
          </IconButton>
        }
      >
        <Box width={"264px"} sx={{ boxShadow: 2, borderRadius: Radius.L }}>
          <Box padding={Spacing.XL}>
            <Box mb={Spacing.M}>
              {profilePicture ? (
                <UserProfilePictureIcon profilePicture={profilePicture} variant="large" />
              ) : (
                <UserInitialsAvatar firstName={user?.firstName} surname={user?.lastName} variant="large" />
              )}
            </Box>
            <Typography fontWeight={600} variant="body2">
              {fullName}
            </Typography>
            <Typography color="secondary" variant="body1">
              {user?.email}
            </Typography>
          </Box>
          <Divider />
          <Stack padding={Spacing.XL} spacing={Spacing.L}>
            <Box display="flex" flexDirection="row" alignItems="center">
              <PhoneIcon />
              <Typography marginLeft={Spacing.S} variant="body2">
                {user?.phone}
              </Typography>
            </Box>
            <Box display="flex" flexDirection="row" alignItems="center">
              <AtIcon />
              <Typography marginLeft={Spacing.S} variant="body2">
                {user?.email}
              </Typography>
            </Box>
          </Stack>
          <Divider />
          <Stack paddingY={Spacing.L} paddingX={Spacing.S}>
            <UnstyledLink target="_blank" to="https://www.zfpgroup.cz/">
              <StyledLink fullWidth>
                <Box display="flex" flexDirection="row" alignItems="center" paddingLeft={Spacing.L}>
                  <HomeIcon color={theme.colors.textSecondary} />
                  <Typography fontWeight={500} marginLeft={Spacing.S} variant="body2">
                    ZFP Group
                  </Typography>
                </Box>
              </StyledLink>
            </UnstyledLink>
            <StyledLink fullWidth onClick={() => navigate(AppRouting.FAQ.path())}>
              <Box display="flex" flexDirection="row" alignItems="center" paddingLeft={Spacing.L}>
                <HelpOutlineOutlinedIcon sx={{ fontSize: "19px", color: "grey.500" }} />
                <Typography fontWeight={500} marginLeft={Spacing.S} variant="body2">
                  {t(Resources.ReportIssue.Title)}
                </Typography>
              </Box>
            </StyledLink>
            {isDevelopment && (
              <StyledLink fullWidth onClick={handleOpenDevTools}>
                <Box display="flex" flexDirection="row" alignItems="center" paddingLeft={Spacing.L}>
                  <CodeIcon sx={{ fontSize: "19px", color: "grey.500" }} />
                  <Typography fontWeight={500} marginLeft={Spacing.S} variant="body2">
                    {t(Resources.DevTools.Title)}
                  </Typography>
                </Box>
              </StyledLink>
            )}
            <StyledLink fullWidth onClick={handleLogout}>
              <Box display="flex" flexDirection="row" alignItems="center" paddingLeft={Spacing.L}>
                <LogoutIcon />
                <Typography fontWeight={500} marginLeft={Spacing.S} variant="body2">
                  {t(Resources.Common.SignOut)}
                </Typography>
              </Box>
            </StyledLink>
          </Stack>
        </Box>
      </Popper>
      {isDevelopment && <DevTools isOpen={isDevToolsOpen} onClose={() => setIsDevToolsOpen(false)} />}
    </>
  );
};
